import {
  authControllerProcessResetPass as mutationFn,
  ProcessResetPasswordDtoReq,
} from "@/shared/api/generated.ts";
import {
  useAutoTranslation,
  useRecordOptionsStore,
  useUserStore,
} from "@/entities";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LANG_KEYS, QUERY_KEYS, ROUTER_PATHS } from "@/shared/constants";

type FormData = ProcessResetPasswordDtoReq & { confirmPassword: string };
export function useProcessResetPassForm(code: string) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<FormData>();

  const { login } = useUserStore();
  const navigate = useNavigate();
  const { t } = useAutoTranslation();
  const queryClient = useQueryClient();
  const { invalidateOptions } = useRecordOptionsStore();

  const formMutation = useMutation({
    mutationFn,
    async onSuccess({ access_token }) {
      login(access_token);
      reset();
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTES });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FAVORITE });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DELETED });
      await invalidateOptions();
      navigate(ROUTER_PATHS.HOME, { replace: true });
    },
  });

  const NOT_MATCH_PASSWORDS_ERROR = {
    type: "custom",
    message: t(
      LANG_KEYS.PASSWORDS_DONT_MATCH.key,
      LANG_KEYS.PASSWORDS_DONT_MATCH.value,
    ),
  };

  return {
    register,
    handleSubmit: handleSubmit((values) => {
      if (values.password !== values.confirmPassword) {
        setError("password", NOT_MATCH_PASSWORDS_ERROR);
        setError("confirmPassword", NOT_MATCH_PASSWORDS_ERROR);
        return;
      }
      formMutation.mutate({
        password: values.password,
        code,
      });
    }),
    isLoading: formMutation.isPending,
    errors,
  };
}
